import { Col, Form, Image, Row, Select } from "antd"
import { type FormInstance } from "antd/es/form/hooks/useForm"
import React from "react"

import { PhoneInput } from "../../../../../components/PhoneInputs/PhoneInput.tsx"
import { DocumentCard } from "../../../../../components/Proposal&Contracts/DocumentCard.tsx"
import { placeholders } from "../../../../../constants/placeholders"
import { normalizeEmail } from "../../../../../utils/form/normalizeEmail.tsx"
import { validatePhone } from "../../../../../utils/validation/validate-phone.ts"
import { zipCodeRegexUS } from "../../../../../utils/validation/zip-code.ts"
import { proposalFilterOptions } from "../../../constants/proposal-filter-options.ts"
import type {
  ProposalFormCompany,
  ProposalFormData
} from "../proposal-form-data.tsx"
import { TextArea } from "../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../components/Input/TextInput.tsx"

interface ProposalCompanySectionProps {
  form: FormInstance<ProposalFormData>
}

export function ProposalCompanySection({ form }: ProposalCompanySectionProps) {
  const key: keyof ProposalFormData = "company"
  const logoKey = "logo_url" as keyof ProposalFormCompany
  const logoSrc = Form.useWatch([key, logoKey], form)

  const isDisableAllFiles = true

  return (
    <DocumentCard title="Company Information" name={key} withToggle={false}>
      <Row gutter={16}>
        <Col>
          <Form.Item<ProposalFormData> name={[key, "logo_url"]}>
            <Image
              preview
              src={logoSrc || "/placeholder.svg"}
              placeholder={
                <Image
                  preview={false}
                  alt="placeholder image"
                  src="/placeholder.svg"
                  width={144}
                  className={
                    "size-36 object-cover rounded-md border border-dashed border-border"
                  }
                />
              }
              className={
                "size-36 object-cover rounded-md border border-dashed border-border"
              }
            />
          </Form.Item>
        </Col>

        <Col flex="auto">
          <Row gutter={[12, 0]}>
            <Col span={12}>
              <Form.Item<ProposalFormData>
                name={[key, "company_name"]}
                label="Company Name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please input company name"
                  }
                ]}
              >
                <TextInput
                  disabled={isDisableAllFiles}
                  placeholder="Enter company name"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<ProposalFormData>
                name={[key, "phone"]}
                label="Phone"
                rules={[{ required: false, validator: validatePhone }]}
              >
                <PhoneInput disabled={isDisableAllFiles} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<ProposalFormData>
                name={[key, "email"]}
                label="Email"
                rules={[
                  {
                    type: "email",
                    message: "The email you entered is incorrect"
                  }
                ]}
                normalize={normalizeEmail}
              >
                <TextInput
                  disableAutoCapitalize
                  disabled={isDisableAllFiles}
                  placeholder={placeholders.email}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<ProposalFormData>
                name={[key, "website"]}
                label="Website"
                rules={[
                  {
                    type: "url",
                    message: "Please enter a valid URL"
                  }
                ]}
              >
                <TextInput
                  disableAutoCapitalize
                  disabled={isDisableAllFiles}
                  placeholder="Enter website"
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Address fields */}
      <Row gutter={[12, 0]}>
        <Col span={12}>
          <Form.Item<ProposalFormData>
            name={[key, "street_address"]}
            label="Street Address"
          >
            <TextInput
              disabled={isDisableAllFiles}
              placeholder="Enter street address"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<ProposalFormData> name={[key, "city"]} label="City">
            <TextInput disabled={isDisableAllFiles} placeholder="Enter city" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<ProposalFormData> name={[key, "state"]} label="State">
            <Select
              disabled={isDisableAllFiles}
              allowClear
              placeholder="Select state"
              options={proposalFilterOptions.state}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input?.toLowerCase())
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item<ProposalFormData>
            name={[key, "zip_code"]}
            label="Zip Code"
            rules={[
              {
                required: false,
                pattern: zipCodeRegexUS,
                message: "Invalid postal code format"
              }
            ]}
          >
            <TextInput
              disableAutoCapitalize
              disabled={isDisableAllFiles}
              placeholder="Enter zip code"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* License and insurance information */}
      <Row gutter={[12, 0]}>
        <Col span={6}>
          <Form.Item<ProposalFormData>
            name={[key, "license_number"]}
            label="License Number"
          >
            <TextInput
              disableAutoCapitalize
              disabled={isDisableAllFiles}
              placeholder="Enter license number"
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<ProposalFormData>
            name={[key, "bond_number"]}
            label="Bond Number"
          >
            <TextInput
              disableAutoCapitalize
              disabled={isDisableAllFiles}
              placeholder="Enter bond number"
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<ProposalFormData>
            name={[key, "workers_comp_number"]}
            label="Workers Comp Number"
          >
            <TextInput
              disableAutoCapitalize
              disabled={isDisableAllFiles}
              placeholder="Enter workers comp number"
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item<ProposalFormData>
            name={[key, "liability_number"]}
            label="Liability Number"
          >
            <TextInput
              disableAutoCapitalize
              disabled={isDisableAllFiles}
              placeholder="Enter liability number"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Company bio */}
      <Form.Item<ProposalFormData>
        name={[key, "company_bio"]}
        label="Company Bio"
      >
        <TextArea
          disabled={isDisableAllFiles}
          rows={3}
          placeholder="Enter company bio..."
        />
      </Form.Item>
    </DocumentCard>
  )
}
